import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/root-reducer';
import {
    getPreselectionSubmissions,
    sendSelectionEmail,
    updateVideoManagementFilters,
    setSelectedVideos,
    clearSelectedVideos,
    preselectSubmission,
} from '../../redux/contest/actions';
import { IVideoManagementFilters } from './types';

export const useVideoManagement = (contestId: string) => {
    const dispatch = useDispatch();

    // Select state from Redux
    const {
        preselectionSubmissions,
        gettingPreselectionSubmissions,
        preselectionPagination,
        preselectionError,
        selectedVideos,
        videoManagementFilters,
        sendingSelectionEmail,
    } = useSelector((state: RootState) => state.contest);

    // Local state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch submissions
    const fetchSubmissions = useCallback(
        (filters?: Partial<IVideoManagementFilters>, page?: number, limit?: number) => {
            const updatedFilters = { ...videoManagementFilters, ...filters };
            // Always reset to page 1 when filters change
            if (filters && Object.keys(filters).some(key => key !== 'page')) {
                updatedFilters.page = 1;
            }

            dispatch(
                getPreselectionSubmissions({
                    contestId,
                    ...updatedFilters,
                    ...(page && { page }),
                    ...(limit && { limit }),
                }),
            );
        },
        [contestId, videoManagementFilters, dispatch],
    );

    // Handle video selection
    const handleVideoSelect = useCallback(
        (videoId: string, checked: boolean) => {
            if (checked) {
                dispatch(setSelectedVideos({ videoIds: [...selectedVideos, videoId] }));
            } else {
                dispatch(
                    setSelectedVideos({
                        videoIds: selectedVideos.filter(id => id !== videoId),
                    }),
                );
            }
        },
        [selectedVideos, dispatch],
    );

    // Handle select all
    const handleSelectAll = useCallback(() => {
        if (selectedVideos && preselectionSubmissions && selectedVideos.length === preselectionSubmissions.length) {
            dispatch(clearSelectedVideos());
        } else {
            dispatch(
                setSelectedVideos({
                    videoIds: preselectionSubmissions.map(video => video._id),
                }),
            );
        }
    }, [selectedVideos, preselectionSubmissions, dispatch]);

    // Handle send email
    const handleSendEmail = useCallback(() => {
        if (selectedVideos?.length === 0) return;

        dispatch(
            sendSelectionEmail({
                contestId,
                submissionIds: selectedVideos,
            }),
        );
    }, [contestId, selectedVideos, dispatch]);

    // Handle filters change
    const handleFiltersChange = useCallback(
        (filters: Partial<IVideoManagementFilters>) => {
            dispatch(updateVideoManagementFilters({ filters }));
            fetchSubmissions(filters);
        },
        [dispatch, fetchSubmissions],
    );

    const handleOfficialSelection = useCallback(
        (submissionId: string[], selectionState: 'selected' | 'not_selected') => {
            dispatch(
                preselectSubmission({
                    contestId: contestId,
                    selection_state: selectionState,
                    submissionIds: submissionId,
                }),
            );
        },
        [dispatch, contestId],
    );

    // Open modal and fetch data
    const openModal = useCallback(() => {
        fetchSubmissions(undefined, 1, 9);
        setIsModalOpen(true);
    }, [fetchSubmissions]);

    // Close modal
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        dispatch(clearSelectedVideos());
    }, [dispatch]);

    return {
        // State
        submissions: preselectionSubmissions,
        loading: gettingPreselectionSubmissions,
        pagination: preselectionPagination,
        error: preselectionError,
        selectedVideos,
        filters: videoManagementFilters,
        sendingEmail: sendingSelectionEmail,
        isModalOpen,

        // Actions
        openModal,
        closeModal,
        fetchSubmissions,
        handleVideoSelect,
        handleSelectAll,
        handleSendEmail,
        handleFiltersChange,
        handleOfficialSelection,
    };
};
